/**
 * Audio Mixing Utilities
 *
 * Shared validation and utility functions for audio mixing configuration.
 * Used by both the admin UI (Settings, ShowEdit) and edge functions.
 *
 * @module mixing-utils
 * @version 1.0 - Issue #400 Per-show mixing spec
 */

import type { MixingTrack } from './types/content.ts';

export interface MixingValidationError {
  message: string;
  path?: string;
}

/**
 * Default mix spec template used when no config is found.
 * Defines a standard 4-track mix: intro, voice, optional music, and optional outro.
 */
export const DEFAULT_MIX_SPEC: MixingTrack[] = [
  {
    id: 'intro',
    source: '{{intro-music-path}}',
    keyframes: [0, 1, 6, 1, 8, 0.2, -0.5, 0.2, 0, 0],
  },
  {
    id: 'voice',
    source: '{{voice-path}}',
    offset: 'intro start 8.5',
  },
  {
    id: 'music',
    source: '{{music-path}}',
    keyframes: [0, 0.1, -3, 0.1, 0, 0],
  },
  {
    id: 'outro',
    source: '{{outro-music-path}}',
    offset: 'voice end 0',
    keyframes: [0, 0, 0.5, 1, -3, 1, 0, 0],
  },
];

/**
 * Validates a mixing spec (tracks array) schema.
 * Checks for:
 * - Array structure with at least one track
 * - Required fields (source)
 * - Field types (id, clipStart, clipEnd, offset, keyframes)
 * - Offset string format ("trackId anchor [shift]")
 * - Keyframe pairs with valid volume values (0-1)
 * - Circular reference detection in track offsets
 *
 * @param tracks - The tracks array to validate
 * @returns Array of validation errors (empty if valid)
 */
export function validateMixingSpec(tracks: unknown): MixingValidationError[] {
  const errors: MixingValidationError[] = [];

  if (!Array.isArray(tracks)) {
    return [{ message: 'Config must be an array of tracks' }];
  }

  if (tracks.length === 0) {
    return [{ message: 'At least one track is required' }];
  }

  // Build id set for reference checking
  const trackIds = new Set<string>();
  tracks.forEach((track, index) => {
    if (track.id && typeof track.id === 'string') {
      if (trackIds.has(track.id)) {
        errors.push({
          message: `Duplicate track id: "${track.id}"`,
          path: `[${index}].id`,
        });
      }
      trackIds.add(track.id);
    }
  });

  // Validate each track
  tracks.forEach((track, index) => {
    const path = `[${index}]`;

    // source is required
    if (!track.source || typeof track.source !== 'string') {
      errors.push({
        message: 'source is required and must be a string',
        path: `${path}.source`,
      });
    }

    // id must be string if present
    if (track.id !== undefined && typeof track.id !== 'string') {
      errors.push({ message: 'id must be a string', path: `${path}.id` });
    }

    // clipStart must be number if present
    if (track.clipStart !== undefined && typeof track.clipStart !== 'number') {
      errors.push({
        message: 'clipStart must be a number',
        path: `${path}.clipStart`,
      });
    }

    // clipEnd must be number if present
    if (track.clipEnd !== undefined && typeof track.clipEnd !== 'number') {
      errors.push({
        message: 'clipEnd must be a number',
        path: `${path}.clipEnd`,
      });
    }

    // offset must be number or valid string format
    if (track.offset !== undefined) {
      if (typeof track.offset !== 'number' && typeof track.offset !== 'string') {
        errors.push({
          message: 'offset must be a number or string',
          path: `${path}.offset`,
        });
      } else if (typeof track.offset === 'string') {
        // Validate string format: "trackId anchor shift"
        const parts = track.offset.trim().split(/\s+/);
        if (parts.length < 2) {
          errors.push({
            message: 'offset string must be "trackId anchor [shift]"',
            path: `${path}.offset`,
          });
        } else {
          const [refId, anchor] = parts;
          if (anchor !== 'start' && anchor !== 'end') {
            errors.push({
              message: 'offset anchor must be "start" or "end"',
              path: `${path}.offset`,
            });
          }
          if (refId && !trackIds.has(refId)) {
            errors.push({
              message: `offset references unknown track: "${refId}"`,
              path: `${path}.offset`,
            });
          }
          if (parts.length >= 3 && Number.isNaN(parseFloat(parts[2] || ''))) {
            errors.push({
              message: 'offset shift must be a valid number',
              path: `${path}.offset`,
            });
          }
        }
      }
    }

    // keyframes must be array with even length, values 0-1
    if (track.keyframes !== undefined) {
      if (!Array.isArray(track.keyframes)) {
        errors.push({
          message: 'keyframes must be an array',
          path: `${path}.keyframes`,
        });
      } else {
        if (track.keyframes.length % 2 !== 0) {
          errors.push({
            message: 'keyframes must have even length (pairs of [time, volume])',
            path: `${path}.keyframes`,
          });
        }
        for (let i = 0; i < track.keyframes.length; i++) {
          if (typeof track.keyframes[i] !== 'number') {
            errors.push({
              message: `keyframes[${i}] must be a number`,
              path: `${path}.keyframes[${i}]`,
            });
          }
          // Volume values (odd indices) must be 0-1
          if (i % 2 === 1) {
            const vol = track.keyframes[i];
            if (typeof vol === 'number' && (vol < 0 || vol > 1)) {
              errors.push({
                message: `keyframes volume at index ${i} must be between 0 and 1`,
                path: `${path}.keyframes[${i}]`,
              });
            }
          }
        }
      }
    }
  });

  // Check for circular references
  const graph = new Map<string, string[]>();
  tracks.forEach((track) => {
    if (track.id) {
      graph.set(track.id, []);
    }
  });

  tracks.forEach((track) => {
    if (typeof track.offset === 'string') {
      const parts = track.offset.trim().split(/\s+/);
      const refId = parts[0];
      if (refId && track.id && graph.has(refId)) {
        graph.get(track.id)?.push(refId);
      }
    }
  });

  // Simple cycle detection using DFS
  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  function hasCycle(nodeId: string): boolean {
    if (recursionStack.has(nodeId)) return true;
    if (visited.has(nodeId)) return false;

    visited.add(nodeId);
    recursionStack.add(nodeId);

    const deps = graph.get(nodeId) || [];
    for (const dep of deps) {
      if (hasCycle(dep)) return true;
    }

    recursionStack.delete(nodeId);
    return false;
  }

  for (const id of graph.keys()) {
    if (hasCycle(id)) {
      errors.push({ message: 'Circular reference detected in track offsets' });
      break;
    }
  }

  return errors;
}

/**
 * Formats a mixing spec as JSON with compact numeric arrays.
 * Arrays containing only numbers (like keyframes) are kept on a single line.
 *
 * @param value - The mixing spec to format
 * @returns Pretty-printed JSON string with compact arrays
 */
export function formatMixingSpecJson(value: unknown): string {
  const json = JSON.stringify(value, null, 2);

  // Collapse numeric arrays onto single lines
  // Matches arrays that span multiple lines and contain only numbers
  return json.replace(
    /\[\s*\n(\s*-?\d+\.?\d*,?\s*\n)+\s*\]/g,
    (match) => {
      // Extract numbers from the multi-line array
      const numbers = match.match(/-?\d+\.?\d*/g);
      return numbers ? `[${numbers.join(', ')}]` : match;
    }
  );
}
